import { useEffect, useRef, useState, ChangeEvent } from "react";

import { Textarea } from "@/components/ui/textarea";

import { MatrixHeaderFieldProps } from "./types";

// A column header wraps between words only, so the table never squeezes a
// column below its longest word ("Refreshmen t"), and it grows to its whole
// text rather than clipping (B-UI-MISC U12).
const MATRIX_HEADER_FIELD_CLASSES =
  "block h-auto min-h-0 w-full max-w-full resize-none whitespace-normal break-words border-0 bg-transparent px-0 py-0 text-center text-[11px] leading-[1.3] font-medium text-muted-foreground shadow-none focus-visible:ring-0 [field-sizing:content]";

const MATRIX_ROW_FIELD_CLASSES =
  "block h-auto min-h-[22px] w-full resize-none overflow-hidden whitespace-normal break-words [overflow-wrap:anywhere] border-0 bg-transparent px-0 py-0 text-[12.5px] font-medium text-foreground shadow-none focus-visible:ring-0 [field-sizing:content]";

export function MatrixHeaderField(props: MatrixHeaderFieldProps) {
  const {
    value,
    onChange,
    commitMode,
    variant,
    disabled,
    maxLength,
    ariaLabel,
    placeholder,
    dataTestId,
  } = props;

  const [draft, setDraft] = useState(value);
  const isFocusedRef = useRef(false);

  useEffect(() => {
    if (!isFocusedRef.current) {
      setDraft(value);
    }
  }, [value]);

  const fieldClass =
    variant === "column"
      ? MATRIX_HEADER_FIELD_CLASSES
      : MATRIX_ROW_FIELD_CLASSES;
  const displayValue = commitMode === "blur" ? draft : value;

  function handleChange(event: ChangeEvent<HTMLTextAreaElement>) {
    const next = event.target.value;
    if (commitMode === "change") {
      onChange(next);
      return;
    }
    setDraft(next);
  }

  function handleFocus() {
    isFocusedRef.current = true;
  }

  function handleBlur() {
    isFocusedRef.current = false;
    if (commitMode !== "blur") {
      return;
    }
    const next = draft.trim();
    if (next !== value) {
      onChange(next);
    }
  }

  return (
    <Textarea
      value={displayValue}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      rows={1}
      maxLength={maxLength}
      disabled={disabled}
      aria-label={ariaLabel}
      placeholder={placeholder}
      title={displayValue}
      className={fieldClass}
      data-testid={dataTestId}
    />
  );
}
